import { useTranslation } from "react-i18next";
import { categoryCounts, regionCounts, type StationV2 } from "@orvis/core";
import type { BrowseFilterState } from "../hooks/useBrowseFilter.js";
import { geolocationSupported } from "../hooks/useGeolocation.js";
import { Chip, ChipRow } from "./Chips.js";

/**
 * Category chips (TV + Radio) and region / near-me chips (Radio). Rows
 * only render when they'd offer a real choice — a list where everything
 * is "general" gets no chips at all.
 */
export function BrowseFilters({
  stations,
  filter,
  onChange,
  showRegions = false,
  geoStatus,
}: {
  stations: readonly StationV2[];
  filter: BrowseFilterState;
  onChange: (patch: Partial<BrowseFilterState>) => void;
  showRegions?: boolean;
  geoStatus?: "idle" | "locating" | "denied" | "error";
}) {
  const { t } = useTranslation();
  const cats = categoryCounts(stations);
  const regions = showRegions ? regionCounts(stations).slice(0, 12) : [];
  const canNear = showRegions && geolocationSupported() && stations.some((s) => s.geo);

  const showCats = cats.length >= 2 || !!filter.category;
  const showRegionRow = regions.length >= 2 || canNear || !!filter.region;
  if (!showCats && !showRegionRow) return null;

  return (
    <div className="space-y-1">
      {showCats ? (
        <ChipRow label={t("filter_category")}>
          <Chip active={!filter.category} onClick={() => onChange({ category: undefined })}>
            {t("filter_all")}
          </Chip>
          {cats.map((c) => (
            <Chip
              key={c.id}
              active={filter.category === c.id}
              onClick={() => onChange({ category: filter.category === c.id ? undefined : c.id })}
            >
              {t(`category_${c.id}`)}
            </Chip>
          ))}
        </ChipRow>
      ) : null}
      {showRegionRow ? (
        <ChipRow label={t("filter_region")}>
          {canNear ? (
            <Chip active={filter.near} onClick={() => onChange({ near: !filter.near, region: undefined })}>
              {"📍 "}
              {geoStatus === "locating" ? t("near_me_locating") : t("near_me")}
            </Chip>
          ) : null}
          {regions.map((r) => (
            <Chip
              key={r.region}
              active={filter.region === r.region}
              onClick={() => onChange({ region: filter.region === r.region ? undefined : r.region, near: false })}
            >
              {r.region}
            </Chip>
          ))}
        </ChipRow>
      ) : null}
      {filter.near && geoStatus === "denied" ? (
        <div className="text-xs text-text/60 px-1">{t("near_me_denied")}</div>
      ) : null}
    </div>
  );
}
